import { useState, useMemo, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  User, Mail, Phone, ChevronDown, MessageSquare, CheckCircle2,
  AlertCircle, Target, Zap, Users, Clock, Lock, LogIn,
} from 'lucide-react'
import { masterclassService } from '../../services/masterclassService'
import {
  getTrackingContext,
  trackMasterclassRegStarted,
  trackMasterclassRegCompleted,
} from '../../services/websiteTracker'
import LoadingSpinner from '../LoadingSpinner'

const INTERESTS = [
  'Photography', 'Videography', 'Graphic Design', 'Motion Graphics',
  'Web Design', 'Digital Marketing', 'AI', 'Film & TV', 'Music Production',
  'Content Creation', 'Entrepreneurship', 'Other',
]
const GENDERS = ['Male', 'Female', 'Prefer not to say']

const GENDER_OPTIONS = GENDERS.map(g => ({ value: g, label: g }))
const INTEREST_OPTIONS = INTERESTS.map(c => ({ value: c, label: c }))

const REQUIRED_FIELDS = [
  'first_name', 'last_name', 'email', 'phone', 'gender', 'age',
  'current_course',
]

const initialForm = {
  first_name: '', last_name: '', email: '', phone: '',
  gender: '', age: '', current_course: '',
  questions: '', agree_reminders: false,
}

const FIELD_BORDER =
  'border-white/10 hover:border-cinema-glow focus:border-cinema-glow focus:shadow-[0_0_0_4px_rgba(238,141,33,0.12)]'

function CardHeader({ icon: Icon, title }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-cinema-glow/15">
          <Icon className="w-4 h-4 text-cinema-glow" strokeWidth={1.5} aria-hidden="true" />
        </div>
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>
      <div className="h-px w-full bg-cinema-glow/40" />
    </div>
  )
}

function FieldLabel({ children, required, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block font-sans text-[11px] uppercase tracking-[2px] font-bold text-cinema-gold mb-2"
    >
      {children}
      {required && <span className="text-cinema-glow ml-0.5">*</span>}
    </label>
  )
}

function FieldError({ message }) {
  if (!message) return null
  return (
    <p className="text-red-400 text-xs font-sans mt-1.5 flex items-center gap-1.5">
      <AlertCircle className="w-3 h-3 shrink-0" />
      {message}
    </p>
  )
}

function CustomSelect({ id, value, onChange, options, placeholder, error }) {
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const containerRef = useRef(null)
  const buttonRef = useRef(null)
  const listRef = useRef(null)

  const selected = options.find(o => o.value === value)

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (open) {
      const idx = options.findIndex(o => o.value === value)
      setHighlighted(idx >= 0 ? idx : 0)
    }
  }, [open, options, value])

  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('[data-highlighted="true"]')
      active?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlighted, open])

  const handleKeyDown = (e) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        buttonRef.current?.focus()
        break
      case 'ArrowDown':
        e.preventDefault()
        setHighlighted(i => (i + 1) % options.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlighted(i => (i - 1 + options.length) % options.length)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        onChange(options[highlighted].value)
        setOpen(false)
        buttonRef.current?.focus()
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        break
    }
  }

  const selectValue = (next) => {
    onChange(next)
    setOpen(false)
    buttonRef.current?.focus()
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        ref={buttonRef}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleKeyDown}
        className={`w-full h-[56px] bg-white/[0.02] border px-4 text-left flex items-center justify-between
          transition-all duration-[250ms] ease focus:outline-none
          ${error
            ? 'border-red-500 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.12)]'
            : FIELD_BORDER
          }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={error ? 'true' : 'false'}
      >
        <span className={selected ? 'text-white font-sans text-sm' : 'text-white/35 font-sans text-sm'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-white/35 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-cinema-graphite border border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.5)] max-h-60 overflow-auto outline-none"
          role="listbox"
          aria-labelledby={id}
          tabIndex={-1}
        >
          {options.map((option, idx) => {
            const isSelected = value === option.value
            const isHighlighted = highlighted === idx
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                data-highlighted={isHighlighted}
                onClick={() => selectValue(option.value)}
                onMouseEnter={() => setHighlighted(idx)}
                className={`px-4 py-3 text-sm font-sans cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-cinema-glow/20 text-white'
                    : isHighlighted
                    ? 'bg-white/10 text-white'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                {option.label}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default function MasterclassRegistrationForm({ masterclass, onSuccess }) {
  const [form, setForm] = useState(initialForm)
  const [touched, setTouched] = useState({})
  const [attempted, setAttempted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }))
    setTouched(t => ({ ...t, [k]: true }))
  }

  const errors = useMemo(() => {
    const next = {}
    REQUIRED_FIELDS.forEach(field => {
      const value = form[field]
      if (!value || (typeof value === 'string' && !value.trim())) {
        next[field] = 'This field is required'
      }
    })
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = 'Please enter a valid email address'
    }
    if (form.age && (Number(form.age) < 14 || Number(form.age) > 70)) {
      next.age = 'Age must be between 14 and 70'
    }
    if (!form.agree_reminders) {
      next.agree_reminders = 'Please agree to receive event reminders'
    }
    return next
  }, [form])

  const isValid = Object.keys(errors).length === 0
  const showError = (field) => (attempted || touched[field]) && errors[field]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setAttempted(true)
    if (!isValid) {
      const firstInvalid = document.querySelector('[aria-invalid="true"]')
      firstInvalid?.focus()
      return
    }
    setError('')
    setLoading(true)
    try {
      const tracking = getTrackingContext()
      const data = await masterclassService.register({
        ...form,
        masterclass_id: masterclass?.id,
        is_current_student: false,
        level: null,
        visitor_id: tracking.visitorId,
        session_id: tracking.sessionId,
        utm_source: tracking.utmSource,
        utm_medium: tracking.utmMedium,
        utm_campaign: tracking.utmCampaign,
        utm_term: tracking.utmTerm,
        utm_content: tracking.utmContent,
        landing_page: typeof window !== 'undefined' ? window.location.pathname : null,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
      })
      trackMasterclassRegCompleted(masterclass?.id, data?.registration?.registration_number)
      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
        onSuccess(data.registration)
      }, 700)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const inputBase = (field) => [
    'w-full h-[56px] bg-white/[0.02] border px-4 text-white placeholder-white/35',
    'transition-all duration-[250ms] ease',
    'focus:outline-none',
    showError(field)
      ? 'border-red-500 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.12)]'
      : FIELD_BORDER,
  ].join(' ')

  const inputProps = (field, type = 'text') => ({
    id: field,
    name: field,
    type,
    value: form[field],
    onChange: e => set(field, e.target.value),
    'aria-invalid': showError(field) ? 'true' : 'false',
    'aria-describedby': showError(field) ? `${field}-error` : undefined,
    className: inputBase(field),
  })

  return (
    <div className="w-full max-w-[1280px] mx-auto rounded-sm border border-white/10 p-5 md:p-6 lg:p-8 bg-gradient-to-b from-cinema-charcoal to-cinema-black">
      <form onSubmit={handleSubmit} className="space-y-7" noValidate>
        {/* Masterclass Pricing & Access Notice */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 bg-cinema-glow/10 border border-cinema-glow/30 p-4 rounded-sm">
            <LogIn className="w-5 h-5 text-cinema-glow shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-bold text-sm">Current SMA Students</p>
              <p className="text-white/80 text-xs mt-1">
                <strong className="text-green-400">FREE Admission</strong> — Register instantly from your{" "}
                <Link to="/student/library" className="text-cinema-glow underline hover:text-cinema-amber font-medium transition-colors">
                  Student Portal
                </Link>
                .
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-white/[0.03] border border-white/10 p-4 rounded-sm">
            <Zap className="w-5 h-5 text-cinema-gold shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-bold text-sm">Non-SMA Guests &amp; Public</p>
              <p className="text-white/80 text-xs mt-1">
                <strong className="text-cinema-gold">UGX 150,000</strong> — Submit this registration form, then complete payment proof verification on your pass.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div
            className="flex items-center gap-3 bg-red-950/40 border border-red-500/20 rounded-sm p-4 text-red-400 text-sm font-sans"
            role="alert"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
          {/* Personal Information */}
          <div className="rounded-sm bg-cinema-graphite border border-white/10 p-7 space-y-6">
            <CardHeader icon={User} title="Personal Information" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <FieldLabel htmlFor="first_name" required>First Name</FieldLabel>
                <input
                  {...inputProps('first_name')}
                  placeholder="John"
                  required
                />
                <FieldError message={showError('first_name') ? errors.first_name : ''} />
              </div>
              <div>
                <FieldLabel htmlFor="last_name" required>Last Name</FieldLabel>
                <input
                  {...inputProps('last_name')}
                  placeholder="Doe"
                  required
                />
                <FieldError message={showError('last_name') ? errors.last_name : ''} />
              </div>
            </div>

            <div>
              <FieldLabel htmlFor="email" required>Email Address</FieldLabel>
              <input
                {...inputProps('email', 'email')}
                placeholder="john@example.com"
                required
              />
              <FieldError message={showError('email') ? errors.email : ''} />
            </div>

            <div>
              <FieldLabel htmlFor="phone" required>Phone Number</FieldLabel>
              <input
                {...inputProps('phone', 'tel')}
                placeholder="+256 700 000 000"
                required
              />
              <FieldError message={showError('phone') ? errors.phone : ''} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <FieldLabel htmlFor="gender" required>Gender</FieldLabel>
                <CustomSelect
                  id="gender"
                  value={form.gender}
                  onChange={v => set('gender', v)}
                  options={GENDER_OPTIONS}
                  placeholder="Select"
                  error={showError('gender')}
                />
                <FieldError message={showError('gender') ? errors.gender : ''} />
              </div>
              <div>
                <FieldLabel htmlFor="age" required>Age</FieldLabel>
                <input
                  {...inputProps('age', 'number')}
                  placeholder="e.g. 22"
                  min={14}
                  max={70}
                  required
                />
                <FieldError message={showError('age') ? errors.age : ''} />
              </div>
            </div>
          </div>

          {/* Interests & Questions */}
          <div className="rounded-sm bg-cinema-graphite border border-white/10 p-7 space-y-6">
            <CardHeader icon={Target} title="Your Interests" />

            <div>
              <FieldLabel htmlFor="current_course" required>Area of Interest</FieldLabel>
              <CustomSelect
                id="current_course"
                value={form.current_course}
                onChange={v => set('current_course', v)}
                options={INTEREST_OPTIONS}
                placeholder="Select your area of interest"
                error={showError('current_course')}
              />
              <FieldError message={showError('current_course') ? errors.current_course : ''} />
            </div>

            <div>
              <FieldLabel htmlFor="questions">Questions You'd Like Answered</FieldLabel>
              <textarea
                id="questions"
                name="questions"
                value={form.questions}
                onChange={e => set('questions', e.target.value)}
                placeholder="What topics would you like the speaker to address?"
                className={`${inputBase('questions')} min-h-[140px] py-4 resize-none`}
                aria-invalid="false"
              />
            </div>

            <div className="flex items-center gap-2 text-white/50 text-xs font-sans pt-2">
              <Mail className="w-3 h-3" />
              <span>Not an SMA student yet? This form is for guest attendees. All are welcome!</span>
            </div>
          </div>
        </div>

        {/* Agreement */}
        <div
          className={`rounded-sm bg-cinema-graphite border p-5 ${
            showError('agree_reminders') ? 'border-red-500/40' : 'border-white/10'
          }`}
        >
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                id="agree_reminders"
                name="agree_reminders"
                className="sr-only peer"
                checked={form.agree_reminders}
                onChange={e => set('agree_reminders', e.target.checked)}
                aria-invalid={showError('agree_reminders') ? 'true' : 'false'}
                aria-describedby={showError('agree_reminders') ? 'agree_reminders-error' : undefined}
              />
              <div className="w-6 h-6 rounded-sm border border-white/15 bg-white/[0.02] peer-checked:bg-cinema-glow peer-checked:border-cinema-glow transition-all flex items-center justify-center shrink-0">
                {form.agree_reminders && <CheckCircle2 className="w-4 h-4 text-white" aria-hidden="true" />}
              </div>
            </div>
            <span className="text-sm md:text-base text-white/70 font-sans leading-relaxed group-hover:text-white/90 transition-colors">
              I agree to receive event reminders and updates from Synergy Multimedia Academy.
            </span>
          </label>
          <div id="agree_reminders-error">
            <FieldError message={showError('agree_reminders') ? errors.agree_reminders : ''} />
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading || success}
          className="w-full h-[60px] text-white font-bold text-base flex items-center justify-center gap-2.5
            bg-cinema-glow
            transition-all duration-[250ms] ease
            hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(238,141,33,0.25)] hover:bg-cinema-amber
            disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none disabled:hover:bg-cinema-glow"
          aria-live="polite"
        >
          {success ? (
            <>
              <CheckCircle2 className="w-5 h-5" />
              Registered!
            </>
          ) : loading ? (
            <>
              <LoadingSpinner size="sm" inline />
              Registering…
            </>
          ) : (
            'Register Now'
          )}
        </button>

        {/* Info row */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-white/80 text-sm font-medium">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-cinema-glow" aria-hidden="true" />
            <span>UGX 150,000 (Guests) · FREE (SMA Students)</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-cinema-glow" aria-hidden="true" />
            <span>Limited Seats</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-cinema-glow" aria-hidden="true" />
            <span>Doors Close 15 Minutes Before Start</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-white/60 text-xs font-sans">
          <Lock className="w-3 h-3" aria-hidden="true" />
          <span>Your information is secure and will never be shared.</span>
        </div>
      </form>
    </div>
  )
}
